import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuthStore } from '@/features/auth/authStore'
import { useCurrentUser } from '@/hooks/useAuth'
import { cn } from '@/utils'
import { EMAIL_REGEX } from '@/utils/constants'

import {
  ERoleType,
  EStepperType,
  STEPPER_LABELS,
  USER_LIST_QUERY_KEY,
} from '../constants'
import { useCreateInvitation, useGetRoles } from '../hooks'
import { useUserManagementStore } from '../store'
import {
  checkDuplicateEmailsInBatch,
  runAllInvitationPromises,
  stepper,
} from '../utils'

import { AssignedAccess } from './AssignedAccess'
import { StepAddUsers } from './StepAddUsers'

/**
 * Two-step modal dialog for inviting users
 * Step 1: Add users (name, email, role, message)
 * Step 2: Assign projects/companies (conditional based on role)
 */
export const InviteUsersDialog = React.memo(() => {
  const methods = stepper.useStepper()
  const { data: currentUser } = useCurrentUser()
  const userRoles = useAuthStore.getState().getRoles() as ERoleType[]

  // Get modal state from store
  const {
    currentUserRole,
    addUsersFormData,
    isInviteModalOpen,
    invitationErrorsByIndex,
    isCheckingEmail,
    closeInviteModal,
    setCurrentUserRole,
  } = useUserManagementStore()

  const { roles, availableRoles, ...rest } = useGetRoles({
    enabled: isInviteModalOpen,
  })

  const {
    mutateAsync: createInvitationAsync,
    isPending: isCreatingInvitation,
  } = useCreateInvitation()

  const queryClient = useQueryClient()

  // Initialize user role
  React.useEffect(() => {
    const user = currentUser
    if (!user || !userRoles.length || !!currentUserRole) return

    // Check if the user has the organization admin role
    const isOrgAdmin = userRoles.includes(ERoleType.ORGANIZATION_ADMIN)
    if (isOrgAdmin) {
      setCurrentUserRole(ERoleType.ORGANIZATION_ADMIN)
      return
    }

    // Check if the user has the project lead role
    const isProjectLead = userRoles.includes(ERoleType.PROJECT_LEAD)
    if (isProjectLead) {
      setCurrentUserRole(ERoleType.PROJECT_LEAD)
      return
    }

    // Check if the user has the team member role
    const isTeamMember = userRoles.includes(ERoleType.TEAM_MEMBER)

    if (isTeamMember) setCurrentUserRole(ERoleType.TEAM_MEMBER)
  }, [currentUser, currentUserRole, setCurrentUserRole, userRoles])

  React.useEffect(() => {
    if (!isInviteModalOpen && !!methods) methods.navigation.reset()
  }, [isInviteModalOpen, methods])

  const handleBack = React.useCallback(() => {
    methods.navigation.goTo(EStepperType.ADD_USERS)
  }, [methods.navigation])

  const handleClose = React.useCallback(() => {
    closeInviteModal()
  }, [closeInviteModal])

  const currentStepId = React.useMemo(() => {
    return methods.state.current.data.id
  }, [methods.state])

  const handleSubmit = React.useCallback(() => {
    // If the current step is assigned access, send the invitations
    if (currentStepId === EStepperType.ASSIGNED_ACCESS) {
      // Send the invitations
      runAllInvitationPromises(addUsersFormData, createInvitationAsync).finally(
        () => {
          queryClient.invalidateQueries({
            queryKey: USER_LIST_QUERY_KEY([]),
            refetchType: 'active',
          })

          triggerToast('success', 'Invitation sent successfully')
          closeInviteModal()
        }
      )
      return
    }

    // Navigate to the next step
    methods.navigation.goTo(EStepperType.ASSIGNED_ACCESS)
  }, [
    addUsersFormData,
    closeInviteModal,
    createInvitationAsync,
    currentStepId,
    methods.navigation,
    queryClient,
  ])

  // Check for duplicate emails within the batch
  const duplicateEmailsInBatch = React.useMemo(() => {
    const { duplicates } = checkDuplicateEmailsInBatch(addUsersFormData.users)
    return duplicates
  }, [addUsersFormData.users])

  // Check if form is valid for submission
  const isSubmitValid = React.useMemo(() => {
    // At least one user required
    if (addUsersFormData.users.length === 0) return false

    // Check each user has required fields filled and no errors
    return addUsersFormData.users.every((user) => {
      // Check required fields
      const hasRequiredFields =
        user.firstName.trim() !== '' &&
        user.lastName.trim() !== '' &&
        user.email.trim() !== ''

      // Check no duplicate emails
      const hasDuplicate =
        duplicateEmailsInBatch.get(user.email.toLowerCase().trim()) || false

      // Check email format is valid
      const hasValidEmail = EMAIL_REGEX.test(user.email)
      return hasRequiredFields && !hasDuplicate && hasValidEmail
    })
  }, [addUsersFormData.users, duplicateEmailsInBatch])

  const handleStepClick = React.useCallback(
    (stepId: EStepperType) => {
      // If the next step is assigned access and the form is not valid, do not navigate
      if (stepId === EStepperType.ASSIGNED_ACCESS && !isSubmitValid) return
      // Navigate to the next step
      methods.navigation.goTo(stepId)
    },
    [isSubmitValid, methods.navigation]
  )

  const getSubmitLabel = React.useCallback(() => {
    // If the current step is assigned access, return 'Send'
    if (currentStepId === EStepperType.ASSIGNED_ACCESS) return 'Send'
    return 'Next Step'
  }, [currentStepId])

  return (
    <Dialog open={isInviteModalOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-180 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader>
          <DialogTitle className="font-semibold text-lg text-primary-900 m-0">
            Invite Users
          </DialogTitle>
          <DialogDescription hidden />
        </DialogHeader>

        <stepper.Scoped>
          <div className="flex gap-xl pb-4 border-b border-secondary">
            {methods.state.all.map((step, index) => {
              const isActive = currentStepId === step.id

              return (
                <button
                  key={step.id}
                  className={cn(
                    'flex-1 p-0 bg-transparent border-b-0 border-t-4 pt-3 text-left cursor-pointer',
                    isActive || currentStepId === EStepperType.ASSIGNED_ACCESS
                      ? ' border-brand-alt'
                      : 'border-quaternary'
                  )}
                  onClick={() => handleStepClick(step.id as EStepperType)}
                >
                  <span
                    className={cn(
                      'font-semibold text-sm',
                      isActive
                        ? 'text-brand-secondary-700'
                        : 'text-secondary-700'
                    )}
                  >{`${index + 1}. ${STEPPER_LABELS[step.id as keyof typeof STEPPER_LABELS]}`}</span>
                </button>
              )
            })}
          </div>
          <StepAddUsers
            roles={roles}
            availableRoles={availableRoles}
            stepId={currentStepId}
            teamMemberRole={rest.teamMemberRole}
            orgAdminRole={rest.orgAdminRole}
          />
          <AssignedAccess roles={roles} stepId={currentStepId} />
        </stepper.Scoped>
        <DialogFooter className="w-full">
          {currentStepId === EStepperType.ASSIGNED_ACCESS ? (
            <Button
              variant="outlined"
              color="default"
              onClick={handleBack}
              className="rounded-md mr-auto"
            >
              Back
            </Button>
          ) : null}

          <Button
            variant="outlined"
            color="default"
            onClick={handleClose}
            className="rounded-md"
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            disabled={
              !isSubmitValid ||
              isCreatingInvitation ||
              Object.values(invitationErrorsByIndex).some(Boolean) ||
              isCheckingEmail
            }
            loading={isCreatingInvitation || isCheckingEmail}
            className="rounded-md"
          >
            {getSubmitLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
